import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Hash, Pencil, Check, X, Loader2, AlertCircle } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface GymCodeDisplayProps {
  code: string;
  gymId?: string;
}

const CODE_REGEX = /^[a-z0-9]{6}$/;

export const GymCodeDisplay = ({ code, gymId }: GymCodeDisplayProps) => {
  const { success, error: showError } = useNotifications();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [newCode, setNewCode] = useState(code);
  const [isSaving, setIsSaving] = useState(false);

  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(code);
    success("Código copiado", {
      description: "El código del gimnasio ha sido copiado al portapapeles"
    });
  };

  const handleStartEdit = () => {
    setNewCode(code);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setNewCode(code);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const normalized = newCode.trim().toLowerCase();

    if (!CODE_REGEX.test(normalized)) {
      showError("Código inválido", {
        description: "Debe tener exactamente 6 caracteres alfanuméricos (letras y números).",
      });
      return;
    }

    if (normalized === code.toLowerCase()) {
      setIsEditing(false);
      return;
    }

    if (!gymId) {
      showError("Error", { description: "No se pudo identificar el gimnasio." });
      return;
    }

    setIsSaving(true);
    try {
      // Verificar unicidad
      const { data: existing, error: checkError } = await supabase
        .from("gyms")
        .select("id")
        .ilike("code", normalized)
        .neq("id", gymId)
        .maybeSingle();

      if (checkError) throw checkError;
      if (existing) {
        showError("Código en uso", {
          description: "Ya existe otro gimnasio con ese código. Probá con uno distinto.",
        });
        setIsSaving(false);
        return;
      }

      const { error } = await supabase
        .from("gyms")
        .update({ code: normalized, updated_at: new Date().toISOString() })
        .eq("id", gymId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["managerGyms"] });
      success("Código actualizado", {
        description: "Los usuarios existentes no se ven afectados.",
      });
      setIsEditing(false);
    } catch (e: any) {
      console.error("Error updating gym code:", e);
      showError("Error", {
        description: e.message || "No se pudo actualizar el código.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 backdrop-blur-sm">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-lg bg-primary/10 shrink-0">
            <Hash className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Código del gimnasio
            </p>
            {isEditing ? (
              <Input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.replace(/\s/g, ""))}
                maxLength={6}
                disabled={isSaving}
                placeholder="6 caracteres"
                className="mt-1 h-9 font-mono tracking-widest text-base lowercase"
                autoFocus
              />
            ) : (
              <p className="text-xl font-bold tracking-widest text-foreground font-mono truncate">
                {code}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={handleSave}
                disabled={isSaving}
                className="bg-background/50 hover:bg-background border-border/50"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleCancel}
                disabled={isSaving}
                className="bg-background/50 hover:bg-background border-border/50"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyCode}
                className="bg-background/50 hover:bg-background border-border/50 hover:border-primary/30 transition-all"
              >
                <Copy className="h-4 w-4" />
              </Button>
              {gymId && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleStartEdit}
                  className="bg-background/50 hover:bg-background border-border/50 hover:border-primary/30 transition-all"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="flex items-start gap-2 px-1 text-xs text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/70" />
          <p>
            Cambiar el código sólo afecta a <strong>nuevos registros</strong>. Los usuarios ya
            inscritos siguen perteneciendo al gimnasio sin inconvenientes.
          </p>
        </div>
      )}
    </div>
  );
};
